import { useEffect, useState } from "react";

import { ApiError, getDocument } from "@/api/client";
import type { Document } from "@/api/client";

const IN_FLIGHT: ReadonlySet<string> = new Set(["uploaded", "ingesting"]);
const POLL_MS = 2000;

/**
 * One document, polled while the worker is still ingesting it. Polling stops on a
 * terminal status so an open tab does not hammer the API forever.
 */
export function useDocument(id: string): { doc: Document | null; error: string | null } {
  const [doc, setDoc] = useState<Document | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        const next = await getDocument(id);
        if (cancelled) {
          return;
        }
        setDoc(next);
        setError(null);
        if (IN_FLIGHT.has(next.status)) {
          timer = setTimeout(() => void tick(), POLL_MS);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.detail : "could not reach the API");
        }
      }
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [id]);

  return { doc, error };
}
